import { create } from 'zustand';
import { Feedback, CreateFeedbackPayload } from '@/types/cricket';
import { 
  fetchFeedback,
  createFeedback as createFeedbackAPI,
  updateFeedback as updateFeedbackAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface FeedbackStore {
  feedback: Feedback[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchFeedback: () => Promise<void>;
  createFeedback: (feedback: CreateFeedbackPayload) => Promise<void>;
  updateFeedback: (feedbackId: string, updates: Partial<Feedback>) => Promise<void>;
}

const useFeedbackStore = create<FeedbackStore>((set, get) => ({
  feedback: [],
  loading: false,
  error: null,
  
  fetchFeedback: async () => {
    set({ loading: true, error: null });
    try {
      const feedbackData = await fetchFeedback();
      set({ feedback: feedbackData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching feedback:", error);
      set({ 
        error: message || "Failed to load feedback", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load feedback",
        variant: "destructive",
      });
    }
  },
  
  createFeedback: async (feedback) => {
    try {
      const newFeedback = await createFeedbackAPI(feedback);
      set((state) => ({
        feedback: [newFeedback, ...state.feedback]
      }));
      toast({
        title: "Success",
        description: "Feedback created successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error creating feedback:", error);
      toast({
        title: "Error",
        description: message || "Failed to create feedback",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateFeedback: async (feedbackId, updates) => {
    try {
      const updatedFeedback = await updateFeedbackAPI(feedbackId, updates);
      set((state) => ({
        feedback: state.feedback.map((f) =>
          f.id === feedbackId ? updatedFeedback : f
        ),
      }));
      toast({
        title: 'Success',
        description: 'Feedback updated successfully!',
      });
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update feedback',
        variant: 'destructive',
      });
      throw error;
    }
  },
}));

export default useFeedbackStore; 